/* eslint-disable complexity */

import { defineStore } from 'pinia';
import { watch } from 'vue';
import $fetch from '~/modules/fetch';
import { useCartStore } from './cart';
import { useMeStore } from './me';

const primelabs: any[] = [];

export const useLabExplorationStore = defineStore({
  id: 'lab-exploration',
  state: () => ({
    labs: [],
    filteredLabs: [],
    explore: false,
    loading: false,
    tpqLoading: {},
    empMode: false,
    allowPublic: false,
    filter: { country: [], certification: [] },
    sortFilter: 'price',
    search: '',
    mapMode: false,
    numberOfLabsVisibleOnMap: 0,
    thickness: {},
  }),
  getters: {
    count(s) {
      return s.labs.length;
    },
    isLoading(s) {
      return s.loading;
    },
    countTenderByArea(s) {
      const areas = ['allRegions', 'europe', 'north_america'];

      const availlableLabs = s.labs.filter((lab: any) => lab.isAvailableForTender === 'TRUE');
      const tendersCounts = areas.reduce((acc: any, area: string) => {
        acc[area] = availlableLabs.filter((lab: any) =>
          area !== 'allRegions' ? lab.area === area : true,
        ).length;

        return acc;
      }, {});

      return tendersCounts;
    },
    countVisibleOnMap(s) {
      return s.numberOfLabsVisibleOnMap;
    },
    getLab(s) {
      return (id: string) => s.labs.find((lab: any) => lab.id === id);
    },
    getLabs(s) {
      return (ids: string[]) => s.labs.filter((lab: any) => ids.includes(lab.id as string));
    },
    sortedLabs(s) {
      const labs = s.filteredLabs.slice(0);

      if (['', 'price'].includes(s.sortFilter)) {
        labs.sort((a: any, b: any) => {
          // Make prime be on top
          if (primelabs.includes(a.shopName)) {
            return -1;
          }

          if (primelabs.includes(b.shopName)) {
            return 1;
          }

          // If '-' price, send to last items
          if (a.totalPrice === '-') {
            return 1;
          }

          if (b.totalPrice === '-') {
            return -1;
          }

          if (a.totalPrice.includes(' - ') && !b.totalPrice.includes(' - ')) {
            return 1;
          }

          if (!a.totalPrice.includes(' - ') && b.totalPrice.includes(' - ')) {
            return -1;
          }

          if (
            Number.parseFloat(a.totalPrice as string) < Number.parseFloat(b.totalPrice as string)
          ) {
            return -1;
          }

          if (
            Number.parseFloat(a.totalPrice as string) === Number.parseFloat(b.totalPrice as string)
          ) {
            return 0;
          }

          return 1;
        });
      }

      if (s.sortFilter === 'tat') {
        labs.sort((a: any, b: any) => {
          const aTat = a.offers.reduce((acc: any, offer: any) => {
            if (acc < offer.tat) {
              acc = offer.tat;
            }

            return acc;
          }, 0);
          const bTat = b.offers.reduce((acc: any, offer: any) => {
            if (acc < offer.tat) {
              acc = offer.tat;
            }

            return acc;
          }, 0);

          if (aTat < bTat) {
            return -1;
          }

          if (aTat === bTat) {
            return 0;
          }

          return 1;
        });
      }

      if (s.sortFilter === 'name') {
        labs.sort((a: any, b: any) => (a.shopName || '').localeCompare(b.shopName || ''));
      }

      return labs;
    },
    labsCount(s) {
      const dpLabs = s.labs.filter(
        (lab: any) =>
          Object.values(lab.offers).every(
            (offer: any) =>
              offer.pricingOption === 'ExactPrice' && (offer.price !== '-' || offer.tpq),
          ) && lab.totalPrice !== '-',
      );
      return {
        dpLabs,
        dp: dpLabs.length,
        mb: s.labs.length,
        tender: s.labs.filter((lab: any) => lab.isAvailableForTender === 'TRUE').length,
        labs: s.labs,
      };
    },
  },
  actions: {
    setAllowPublic(mode: any) {
      this.allowPublic = mode;
    },
    setExploreMode(mode: any) {
      this.explore = mode;
    },
    setSearchFilter(search: any) {
      this.search = search;
    },
    setMapMode(mode: any) {
      this.mapMode = mode;
    },
    setSortFilter(filter: any) {
      this.sortFilter = filter;
    },
    async fetchShopsAndCount() {
      const cart = useCartStore();

      this.loading = true;
      try {
        if (this.filter.country.length || this.filter.certification.length) {
          /* We have filter applied, we need to start two calls: one to retrieve to filtered list,
  				the other to get the count of DP/MB/Tender labs */
          const [filtered, labs] = await Promise.all([
            this.fetchShopsV10(this.filter),
            this.fetchShopsV10(),
          ]);
          this.filteredLabs = filtered.shops;
          this.labs = labs.shops;
          this.thickness = filtered.thickness;
        } else {
          const { shops, thickness = {} } = await this.fetchShopsV10();
          this.filteredLabs = shops;
          this.labs = shops;

          Object.keys(this.thickness || []).forEach((id) => {
            if (cart.printItem(id) && !thickness?.[id]) {
              thickness[id] = this.thickness[id];
            }
          });

          this.thickness = thickness;
        }
      } finally {
        // Release the flag even when shopsv10 fails: a rejection used to leave it stuck
        // true, and every later waitGetShopsV10() then waited on a fetch that was over.
        this.loading = false;
      }

      await this.fetchLabsQuotation();

      return this.labs;
    },
    async fetchLabsQuotation() {
      const cart = useCartStore();

      if (cart.printItems.length && this.filteredLabs && true) {
        const processFamilies = cart.printItems
          .map((item: any) => item.ProcessFamily)
          .filter((el: any, index: number, array: any[]) => el && array.indexOf(el) === index); // Filter distinct element

        if (!processFamilies.length) {
          return Promise.resolve([]);
        }

        const shopsTpq = this.filteredLabs
          // Check labs support external quotation
          .filter(
            (lab: any) => lab.ExternalPricingCondition && lab.ExternalPricingCondition !== 'none',
          )
          // Filter labs that can do quotation on selected process families
          .filter((lab: any) =>
            processFamilies.every((el: any) => lab.ExternalPricingCondition.includes(el)),
          );

        // Get quotation for each eligible labs
        const tpqPromises = shopsTpq.map(async (lab: any) => this.fetchLabQuotation(lab));

        return Promise.all(tpqPromises).then((results) => results.filter((el: any) => el));
      }

      return Promise.resolve([]);
    },
    async fetchLabQuotation(lab: { id: any }) {
      const cart = useCartStore();
      const me = useMeStore();

      if (!me.isLoggedIn) {
        return;
      }

      this.tpqLoading[lab.id] = true;

      const buildPostThirdParty = () => {
        const postData = { Items: [], deliveryCountryIso3: cart.shippingAddressCode };
        const printRequests: any[] = [];
        const finihRequests: any[] = [];
        const additionalServiceRequests: any[] = [];

        const { cartItems } = cart;

        cartItems.forEach((cartItem: { type: string }) => {
          if (cartItem.type === 'MP3DP_PrintRequest') {
            printRequests.push(cartItem);
          } else if (cartItem.type === 'MP3DP_FinishRequest') {
            finihRequests.push(cartItem);
          } else if (cartItem.type === 'MP_AdditionalServiceRequest') {
            additionalServiceRequests.push(cartItem);
          }
        });

        printRequests.forEach((printRequest) =>
          postData.Items.push({
            BoundingBoxX: parseFloat(printRequest.BoundingBoxX),
            BoundingBoxY: parseFloat(printRequest.BoundingBoxY),
            BoundingBoxZ: parseFloat(printRequest.BoundingBoxZ),
            Unit: printRequest.Unit,
            Surface: parseFloat(printRequest.Surface),
            id: printRequest.backendId || '',
            Quantity: parseInt(printRequest.Quantity, 10),
            ToleranceMin: parseFloat(printRequest.ToleranceMin) || undefined,
            Volume: parseFloat(printRequest.Volume),
            type: printRequest.type,
            MaterialTypeId: printRequest.MaterialType || '',
            MaterialClassId: printRequest.MaterialSubCategory || '',
            MaterialFamilyId: printRequest.MaterialCategory || '',
            ProcessFamilyId: printRequest.ProcessFamily || '',
            ProcessId: printRequest.Process || '',
            MaterialId: printRequest.MaterialID || '',
          }),
        );

        finihRequests.forEach((finihRequest) => {
          postData.Items.push({
            RelatedCartItemID: finihRequest.RelatedCartItemID,
            id: finihRequest.id || '',
            FinishName: finihRequest.FinishName,
            Quantity: finihRequest.Quantity,
            type: finihRequest.type,
          });
        });

        additionalServiceRequests.forEach((additionalServiceRequest) => {
          postData.Items.push({
            id: additionalServiceRequest.id || '',
            AdditionalServiceName: additionalServiceRequest.AdditionalServiceName,
            Quantity: additionalServiceRequest.Quantity,
            type: additionalServiceRequest.type,
          });
        });
        return postData;
      };

      try {
        const result = (await $fetch.post(`/resources/mdshop/shops/${lab.id}/quotation`, {
          unique: true,
          query: { service: '3dprinting' },
          data: buildPostThirdParty(),
        })) as any;

        Reflect.deleteProperty(this.tpqLoading, lab.id);

        const clonedLab = JSON.parse(JSON.stringify(lab));

        /* * If result price is NaN or < 0 return. */
        if (Number.isNaN(result.price) || result.price < 0) {
          return;
        }

        clonedLab.totalPrice = result.price.toString();

        result.Items.forEach((item: { id: any; OfferId: any }) => {
          const matchOffer = clonedLab.offers.find(
            (offer: { itemId: any }) => offer.itemId === item.id,
          );
          if (matchOffer) {
            // MatchOffer.price = item.unitPrice;
            matchOffer.tat = result.tat;
            matchOffer.tpq = true;
            matchOffer.id = item.OfferId;
            matchOffer.pricingOption = 'ExactPrice';
          }
        });

        const findLab = (this.filteredLabs as any[]).find((el) => el.id === clonedLab.id);
        if (findLab) {
          Object.assign(findLab, clonedLab);
        }
      } catch (e: any) {
        if (e.response && e.response.errorCode === 'Error.Conflict') {
          await new Promise((resolve) => {
            setTimeout(resolve, 2000);
          });
          return this.fetchLabQuotation(lab);
        }
      }
    },
    async fetchShopsV10(customFilter: any = {}) {
      const cart = useCartStore();

      const params = { ...cart.shopsV10Params };
      // Legacy parity: when the cart has no parts (e.g. browsing the providers page), send
      // no `items` key rather than an empty `items: []`, which the shopsv10 backend rejects.
      if (!params.items?.length) {
        delete params.items;
      }

      const isTestMode = window.location.href.includes('test=true');

      if (customFilter.country?.length) {
        params.country = customFilter.country.join(',');
      }

      if (customFilter.certification?.length) {
        params.certifications = customFilter.certification.join(',');
      }

      params.sort = this.sortFilter;

      if (isTestMode) {
        params.test = true;
      }

      const response = await $fetch.post('/resources/mdshop/public/shopsv10', {
        query: {
          service: '3dprinting',
          filter: Boolean(Object.keys(customFilter).length),
        },
        data: params,
      });

      return response;
    },
    resetFilters(s: any) {
      s.filter = { country: [], certification: [] };
    },
    resetLabs(s: any) {
      s.labs = [];
    },
    setEmpMode(payload: boolean) {
      this.empMode = payload;
    },
    // Waiters
    async waitGetShopsV10() {
      if (!this.loading) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        // `this.loading` is a plain boolean, so passing it directly makes watch() an
        // inert no-op (Vue logs "Invalid watch source") and the promise never settles:
        // every caller parked here hangs forever. The source must be a getter to stay
        // reactive.
        const unwatch = watch(
          () => this.loading,
          (isLoading) => {
            if (!isLoading) {
              unwatch();
              resolve();
            }
          },
        );
      });
    },
  },
});
